<script setup lang="ts">
import type { ExportFormat } from "@/types";

import { ref } from "vue";

const props = defineProps<{
  onDelete: boolean;
  selectedCount: number;
  totalCount: number;
  addDisabled: boolean;
}>();

const emit = defineEmits<{
  (e: "open-dialog"): void;
  (e: "open-delete-confirm"): void;
  (e: "cancel-selection"): void;
  (e: "export", format: ExportFormat): void;
  (e: "export-selected", format: ExportFormat): void;
  (e: "open-import", file: File): void;
  (e: "open-import-from-guild"): void;
}>();

const fileInputRef = ref<HTMLInputElement | null>(null);

const onFileChange = (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  emit("open-import", file);
  (event.target as HTMLInputElement).value = "";
};
</script>

<template>
  <div
    class="p-dictionary__import-export-bar d-flex align-center justify-end flex-wrap px-2 py-2 bg-background"
    style="gap: 8px 6px"
  >
    <template v-if="!props.onDelete">
      <v-btn
        prepend-icon="mdi-plus"
        color="success"
        :disabled="props.addDisabled"
        @click="emit('open-dialog')"
      >
        追加
      </v-btn>
      <v-menu>
        <template #activator="{ props: menuProps }">
          <v-btn
            append-icon="mdi-menu-down"
            variant="tonal"
            :disabled="props.totalCount === 0"
            v-bind="menuProps"
          >
            エクスポート
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            :title="`全${props.totalCount}件をエクスポート`"
            disabled
            class="text-caption text-grey"
          />
          <v-divider />
          <v-list-item
            title="CSV"
            @click="emit('export', 'csv')"
          />
          <v-list-item
            title="TSV"
            @click="emit('export', 'tsv')"
          />
          <v-list-item
            title="JSON"
            @click="emit('export', 'json')"
          />
        </v-list>
      </v-menu>
      <v-menu>
        <template #activator="{ props: menuProps }">
          <v-btn
            append-icon="mdi-menu-down"
            variant="tonal"
            v-bind="menuProps"
          >
            インポート
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            title="他のサーバーから"
            @click="emit('open-import-from-guild')"
          />
          <v-list-item
            title="ファイルを選択"
            @click="fileInputRef?.click()"
          >
            <template #subtitle>
              <span class="text-caption">TXT / CSV / TSV / JSON に対応</span>
            </template>
          </v-list-item>
        </v-list>
      </v-menu>
      <input
        ref="fileInputRef"
        type="file"
        accept=".csv,.tsv,.txt,.json,text/csv,text/plain,text/tab-separated-values,application/json"
        style="display: none"
        @change="onFileChange"
      />
    </template>
    <template v-else>
      <v-chip
        variant="tonal"
        size="small"
      >
        {{ props.selectedCount }}件
      </v-chip>
      <v-spacer />
      <v-btn
        color="danger"
        prepend-icon="mdi-trash-can"
        @click="emit('open-delete-confirm')"
      >
        削除
      </v-btn>
      <v-menu>
        <template #activator="{ props: menuProps }">
          <v-btn
            append-icon="mdi-menu-down"
            variant="tonal"
            :disabled="props.selectedCount === 0"
            v-bind="menuProps"
          >
            エクスポート
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            :title="`${props.selectedCount}件をエクスポート`"
            disabled
            class="text-caption text-grey"
          />
          <v-divider />
          <v-list-item
            title="CSV"
            @click="emit('export-selected', 'csv')"
          />
          <v-list-item
            title="TSV"
            @click="emit('export-selected', 'tsv')"
          />
          <v-list-item
            title="JSON"
            @click="emit('export-selected', 'json')"
          />
        </v-list>
      </v-menu>
      <v-btn
        color="grey"
        @click="emit('cancel-selection')"
      >
        キャンセル
      </v-btn>
    </template>
  </div>
</template>
